/**
 * `<caos-provenance-marker>` — marks where a value came from.
 * (Catalog: Status & feedback › ProvenanceMarker — marks where a value came from — computed, overridden, inherited, or synced.)
 *
 * A small badge: a glyph plus an optional `label` (defaulting to the origin's name).
 * `origin` picks the look:
 *  - `overridden` — a human-pinned value over a calculated one: the workbook
 *    yellow-cell tokens `--caos-color-override-*` (orthogonal to brand, theme-fixed),
 *    the intended vocabulary for an override in this design system.
 *  - `computed` — an info tint (calculated by the engine).
 *  - `synced` — a success tint (mirrored from an external system, e.g. GP).
 *  - `inherited` — a neutral tint (flows from a parent/default).
 * Text on each tint reads that tint's THEME-FIXED ink (`-text` / override-text), never
 * the theme-flipping neutral token, so it is legible in both themes (AF2 contrast
 * lesson); the marker is NOT a brand glyph. Styling seams: `::part(marker)`,
 * `::part(icon)`, `::part(label)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon } from './icons.js';

export type CaosProvenanceOrigin = 'computed' | 'overridden' | 'inherited' | 'synced';

const DEFAULT_LABEL: Record<CaosProvenanceOrigin, string> = {
  computed: 'Computed',
  overridden: 'Overridden',
  inherited: 'Inherited',
  synced: 'Synced',
};

const sheet = makeSheet(`
  :host { display: inline-flex; vertical-align: middle; }

  .marker {
    display: inline-flex;
    align-items: center;
    gap: var(--caos-space-1);
    padding: 1px var(--caos-space-2);
    border-radius: var(--caos-radius-pill);
    border: 1px solid transparent;
    font-family: var(--caos-font);
    font-size: 11.5px;
    font-weight: 600;
    line-height: 1.5;
    white-space: nowrap;
    /* default = inherited / neutral */
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text-muted);
    border-color: var(--caos-color-border);
  }

  :host([origin="computed"]) .marker {
    background: var(--caos-color-info-bg);
    color: var(--caos-color-info-text);
    border-color: color-mix(in srgb, var(--caos-color-info) 35%, transparent);
  }
  :host([origin="synced"]) .marker {
    background: var(--caos-color-success-bg);
    color: var(--caos-color-success-text);
    border-color: color-mix(in srgb, var(--caos-color-success) 35%, transparent);
  }
  :host([origin="overridden"]) .marker {
    background: var(--caos-color-override-bg);
    color: var(--caos-color-override-text);
    border-color: var(--caos-color-override-border);
  }

  .icon { flex: none; width: 13px; height: 13px; }
  .icon svg { display: block; width: 100%; height: 100%; }

  .label:empty { display: none; }
`);

export class CaosProvenanceMarker extends CaosElement {
  static get observedAttributes(): string[] {
    return ['origin', 'label'];
  }

  private iconEl: HTMLSpanElement;
  private labelEl: HTMLSpanElement;

  constructor() {
    super(sheet);

    const marker = document.createElement('span');
    marker.className = 'marker';
    marker.setAttribute('part', 'marker');

    this.iconEl = document.createElement('span');
    this.iconEl.className = 'icon';
    this.iconEl.setAttribute('part', 'icon');
    this.iconEl.setAttribute('aria-hidden', 'true');

    this.labelEl = document.createElement('span');
    this.labelEl.className = 'label';
    this.labelEl.setAttribute('part', 'label');

    marker.append(this.iconEl, this.labelEl);
    this.root.append(marker);
  }

  connectedCallback(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'img');
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  private sync(): void {
    const origin = (this.getAttribute('origin') ?? 'inherited') as CaosProvenanceOrigin;
    const iconName =
      origin === 'computed' ? 'computed' :
      origin === 'overridden' ? 'overridden' :
      origin === 'synced' ? 'synced' : 'inherited';
    this.iconEl.replaceChildren(makeIcon(iconName));

    const label = this.getAttribute('label');
    const text = label ?? DEFAULT_LABEL[origin] ?? DEFAULT_LABEL.inherited;
    this.labelEl.textContent = text;
    this.setAttribute('aria-label', `Value origin: ${text}`);
  }
}

define('caos-provenance-marker', CaosProvenanceMarker);

declare global {
  interface HTMLElementTagNameMap {
    'caos-provenance-marker': CaosProvenanceMarker;
  }
}
